import type { MetaDescriptor } from "@remix-run/node";

import { SITE_URL, SUPPORTED_LANGUAGES } from "~/config/constants";
import type { LanguageContextType } from "~/context/LanguageContext";
import { localizeUrl } from "~/helpers/navigationHelper";

type Lang = LanguageContextType["lang"];

/** BCP-47 tags for og:locale. */
const OG_LOCALE: Record<Lang, string> = {
  en: "en_GB",
  fr: "fr_FR",
  de: "de_DE",
  ar: "ar_AR",
};

export type SeoInput = {
  lang: Lang;
  /** Locale-independent path, e.g. "/courses" or "/". */
  path: string;
  title: string;
  description: string;
  /** Absolute URL. Defaults to the social card. */
  image?: string;
  /** Set for pages that must not be indexed. */
  noIndex?: boolean;
  /**
   * The page exists only in English (the blog). Points every locale's canonical
   * at the English URL and noindexes the localized variants, rather than
   * advertising hreflang alternates for translations that do not exist.
   */
  englishOnly?: boolean;
  /** og:type. "article" for blog posts. */
  ogType?: "website" | "article";
};

/**
 * Everything a page needs in <head>: title, description, a real
 * <link rel="canonical">, hreflang alternates for all four locales, and the
 * Open Graph / Twitter pair.
 *
 * The old implementation emitted <meta name="canonical">, which search engines
 * ignore, and hard-coded it to the homepage on every route.
 */
export function buildMeta({
  lang,
  path,
  title,
  description,
  image = `${SITE_URL}/social_post_card.png`,
  noIndex = false,
  englishOnly = false,
  ogType = "website",
}: SeoInput): MetaDescriptor[] {
  const canonical = englishOnly
    ? localizeUrl("en", path)
    : localizeUrl(lang, path);

  const alternates: MetaDescriptor[] = englishOnly
    ? []
    : SUPPORTED_LANGUAGES.map((locale) => ({
        tagName: "link",
        rel: "alternate",
        hrefLang: locale,
        href: localizeUrl(locale, path),
      }));

  if (!englishOnly) {
    // x-default points at the unprefixed English URL, which is what a visitor
    // with an unmatched language should land on.
    alternates.push({
      tagName: "link",
      rel: "alternate",
      hrefLang: "x-default",
      href: localizeUrl("en", path),
    });
  }

  // A localized view of an English-only page is a duplicate, so keep it out of
  // the index while still letting crawlers follow its links.
  const robots =
    noIndex || (englishOnly && lang !== "en")
      ? englishOnly && !noIndex
        ? "noindex, follow"
        : "noindex, nofollow"
      : "index, follow";

  return [
    { title },
    { name: "description", content: description },
    { tagName: "link", rel: "canonical", href: canonical },
    ...alternates,

    { name: "robots", content: robots },

    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: canonical },
    { property: "og:image", content: image },
    { property: "og:type", content: ogType },
    { property: "og:site_name", content: "SkillSynergy" },
    { property: "og:locale", content: OG_LOCALE[lang] },

    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    { name: "twitter:site", content: "@SkillSynergyNet" },
  ];
}

/** Reads the locale out of a route's `params`, falling back to English. */
export function langFromParams(params: { locale?: string }): Lang {
  const locale = params.locale as Lang | undefined;
  return locale && SUPPORTED_LANGUAGES.includes(locale) ? locale : "en";
}
